import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import { OnoffType } from '@/api/enum/common.enum';
import MeditationListSummaryFilterSection from './components/ChartletSummaryFilterSection';
import SetMeditationOrderDialog from './components/SetChartletOrderDialog';
import { useGetChartletPageQuery } from '@/api/treasury/chartletApiSlice';
import {
	ChartletFilter,
	GameCartoonStickerSeriesDTO,
} from '@/api/DTO/treasury/chartlet.interface';
import { showLabel } from '@/utils/helper';
import PublishedChip from '@/common/components/PublishedChip';
import { CHARTLET_TYPE, PUBLISHED_LISTB } from '@/common/constants/common';

export interface DataGrid {
	updatedAt?: string;
	createdAt?: string;
	publishedStartAt?: string;
	publishedEndAt?: string;
	id?: string;
}

export default function ChartletSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();

	const defaultPropertyFilter: ChartletFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		publishedList: [],
	};
	const defaultValues: DefaultValues<ChartletFilter> = defaultPropertyFilter;
	const methods = useForm<ChartletFilter>({
		defaultValues,
	});
	const transformedSearchParams: ChartletFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'publishedList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetChartletPageQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: GameCartoonStickerSeriesDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					publishedStartAt: !item.publishedStartAt
						? '-'
						: dayjs(item.publishedStartAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					publishedEndAt: !item.publishedEndAt
						? '-'
						: dayjs(item.publishedEndAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as ChartletFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('chartletList.seriesTitle'),
			flex: 15,
		},
		{
			field: 'stickerType',
			headerName: t('chartletList.chartletType'),
			flex: 15,
			renderCell: (params: any) =>
				showLabel(params.row.stickerType, CHARTLET_TYPE),
		},
		{
			field: 'rewardName',
			headerName: t('chartletList.collectAllRewards'),
			flex: 15,
			renderCell: (params: any) => params.row.rewardName ?? '-',
		},
		{
			field: 'published',
			headerName: t('chartletList.seriesStatus'),
			flex: 15,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedStartAt',
			headerName: t('chartletList.pushedTime'),
			flex: 10,
		},
		{
			field: 'publishedEndAt',
			headerName: t('chartletList.pulledTime'),
			flex: 10,
		},
		{
			field: 'updatedAt',
			headerName: t('chartletList.updateTime'),
			flex: 10,
		},
		{
			field: 'createdAt',
			headerName: t('chartletList.createTime'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/treasury-manage/chartlet-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.chartletList')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetMeditationOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/treasury-manage/chartlet-list/add`)}
					>
						{t('achievementList.createNewSeries')}
					</Button>
				</Stack>
			</Box>

			<Card sx={{ marginTop: 1 }}>
				<CardContent>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
